import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { parseNewsletterUrl, isNewsletterUrl } from '../lib/newsletterUrl';
import { onlyUpcoming } from '../lib/eventDates';
import { activeThought } from '../lib/thoughts';
import { firstHotTake } from '../lib/hotTakes';
import { firstCurrentlyReading } from '../lib/currentlyReading';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout constants — all coordinates are in this 1080-wide unit system,
// matching the final exported PNG resolution exactly.
// ─────────────────────────────────────────────────────────────────────────────
const CARD_W  = 1080;
const PAD     = 36;
const RADIUS  = 20;
const GAP     = 18;
const BPAD    = 28;
const SANS    = 'var(--font-body)';
const SERIF   = 'var(--font-display)';

const FORMATS = {
  story:  { label: 'Story',  dims: '1080 × 1920', h: 1920, cap: 24 },
  square: { label: 'Square', dims: '1080 × 1080', h: 1080, cap: 12 },
};

const BLOCK_CATALOGUE = [
  { id: 'current_thought',   icon: '💭', name: 'Current Thought',    wt: 2 },
  { id: 'currently_reading', icon: '📖', name: 'Currently Reading',  wt: 3 },
  { id: 'to_know_me',        icon: '💛', name: 'Books That Made Me', wt: 2 },
  { id: 'hot_take',          icon: '🔥', name: 'Hot Take',           wt: 2 },
  { id: 'top_tropes',        icon: '🏷️', name: 'My Top Tropes',      wt: 2 },
  { id: 'reading_stats',     icon: '📊', name: 'Reading Stats',      wt: 5 },
  { id: 'reading_goal',      icon: '🎯', name: 'Reading Goal',       wt: 2 },
  { id: 'ask_me_about',      icon: '💼', name: 'Ask Me About',       wt: 3 },
  { id: 'my_shop',           icon: '🛍️', name: 'My Shop',           wt: 3 },
  { id: 'events',            icon: '📍', name: 'My Events',          wt: 3 },
  { id: 'youtube',           icon: '🎬', name: 'My YouTube',         wt: 3 },
  { id: 'substack',          icon: '✍️', name: 'My newsletter',      wt: 3 },
  { id: 'amazon_storefront', icon: '📦', name: 'Amazon Storefront',  wt: 3 },
  { id: 'recommend_me',      icon: '📚', name: 'Recommend Me',       wt: 3 },
  { id: 'podcast',           icon: '🎙️', name: 'My Podcast',        wt: 3 },
  { id: 'showcase',          icon: '🎨', name: 'Showcase',           wt: 3 },
  { id: 'custom_shelf',      icon: '📚', name: 'Custom Shelf',       wt: 3 },
  // Off by default in the initial `included` pick below (see that useState) --
  // a reflection is private-by-default text, so sharing it has to be a
  // deliberate tap, not something auto-selected because it happens to have
  // content and sort early in this list. Name/emoji match the block's own
  // definition (ReaderProfile.jsx BLOCK_DEFS: 'A Book That...', 📓).
  { id: 'journal',           icon: '📓', name: 'A Book That...',    wt: 3 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Text measurement & wrapping — shared offscreen canvas, no DOM layout involved.
// Returns up to `maxLines` lines, truncating the last with "…" if needed.
// ─────────────────────────────────────────────────────────────────────────────
let _mctx = null;
function ctx() {
  if (!_mctx) _mctx = document.createElement('canvas').getContext('2d');
  return _mctx;
}
function textWidth(text, size, weight, family) {
  const c = ctx();
  c.font = `${weight} ${size}px ${family}`;
  return c.measureText(text || '').width;
}
function wrapText(text, maxWidth, size, weight, family, maxLines = 99) {
  if (!text) return [];
  const words = String(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (!cur || textWidth(test, size, weight, family) <= maxWidth) {
      cur = test;
    } else {
      lines.push(cur);
      cur = w;
      if (lines.length === maxLines) break;
    }
  }
  if (lines.length < maxLines && cur) lines.push(cur);

  const consumedWords = lines.join(' ').split(/\s+/).filter(Boolean).length;
  if (lines.length === maxLines && consumedWords < words.length) {
    let last = lines[maxLines - 1];
    while (last.length > 0 && textWidth(last + '…', size, weight, family) > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last.trimEnd() + '…';
  }
  return lines;
}

// ─────────────────────────────────────────────────────────────────────────────
// Image fetching — convert external URLs to base64 data URIs so the exported
// canvas is never CORS-tainted. Returns null on failure (caller shows fallback).
// ─────────────────────────────────────────────────────────────────────────────
async function toDataUri(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise(resolve => {
      const r = new FileReader();
      r.onload  = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function collectImageUrls(profile, blocks, included, extra) {
  const urls = new Set();
  if (profile?.avatar_url) urls.add(profile.avatar_url);

  included.forEach(id => {
    switch (id) {
      case 'currently_reading': {
        const cr = firstCurrentlyReading(profile);
        const c = cr?.cover_url || cr?.cover_image_url;
        if (c) urls.add(c);
        break;
      }
      case 'to_know_me':
        (profile?.to_know_me || []).filter(b => b?.title).slice(0, 5).forEach(b => {
          const c = b.cover_url || b.cover_image_url;
          if (c) urls.add(c);
        });
        break;
      case 'recommend_me':
        (profile?.recommend_me || []).slice(0, 3).forEach(r => {
          const c = r.cover_url || r.cover_image_url;
          if (c) urls.add(c);
        });
        break;
      case 'youtube': {
        const v = profile?.youtube_data?.videos?.[0];
        if (v?.thumbnail_url) urls.add(v.thumbnail_url);
        break;
      }
      case 'showcase':
        (profile?.showcase_items || []).slice(0, 2).forEach(it => { if (it.url) urls.add(it.url); });
        break;
      case 'custom_shelf': {
        const block = (blocks || []).find(b => b.type === 'custom_shelf');
        (block?.data?.books || []).slice(0, 4).forEach(b => {
          const c = b.cover_url || b.cover_image_url;
          if (c) urls.add(c);
        });
        break;
      }
      case 'my_shop':
        (extra?.shopProducts || []).slice(0, 3).forEach(p => { if (p.image_url) urls.add(p.image_url); });
        break;
      case 'journal': {
        const c = extra?.journalEntry?.book?.cover_image_url;
        if (c) urls.add(c);
        break;
      }
    }
  });
  return Array.from(urls);
}

// ─────────────────────────────────────────────────────────────────────────────
// Content checks — does this block have anything real to show?
// ─────────────────────────────────────────────────────────────────────────────
function blockHasContent(blockType, profile, blocks, extra) {
  switch (blockType) {
    case 'current_thought':   return !!activeThought(profile)?.trim();
    case 'currently_reading': return !!firstCurrentlyReading(profile);
    case 'to_know_me':        return Array.isArray(profile?.to_know_me) && profile.to_know_me.some(b => b?.title);
    case 'hot_take':           return !!firstHotTake(profile);
    case 'top_tropes':         return Array.isArray(profile?.top_tropes) && profile.top_tropes.length > 0;
    case 'reading_goal':       return true;
    case 'reading_stats':      return !!(extra?.readingStats?.books_read);
    case 'ask_me_about':       return Array.isArray(profile?.ask_me_about?.services) && profile.ask_me_about.services.length > 0;
    case 'my_shop':            return !!profile?.shop_url?.trim() || (extra?.shopProducts?.length || 0) > 0;
    case 'events':             return (extra?.events?.length || 0) > 0;
    case 'youtube':            return !!profile?.youtube_data?.videos?.length;
    case 'substack':           return isNewsletterUrl(profile?.substack_url);
    case 'amazon_storefront':  return !!profile?.amazon_storefront?.lists?.length;
    case 'recommend_me':       return Array.isArray(profile?.recommend_me) && profile.recommend_me.length > 0;
    case 'podcast':            return !!profile?.podcast_data?.episodes?.length;
    case 'showcase':           return Array.isArray(profile?.showcase_items) && profile.showcase_items.length > 0;
    case 'custom_shelf': {
      const block = (blocks || []).find(b => b.type === blockType);
      return Array.isArray(block?.data?.books) && block.data.books.length > 0;
    }
    case 'journal':            return !!(extra?.journalEntry?.reflection_text?.trim());
    default: return true;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Small SVG helpers
// ─────────────────────────────────────────────────────────────────────────────
function Frame({ w, h, fill, stroke }) {
  return <rect width={w} height={h} rx={RADIUS} fill={fill} stroke={stroke || 'none'} strokeWidth={stroke ? 2 : 0} />;
}
function Label({ icon, text, color }) {
  return (
    <text x={BPAD} y={BPAD + 20} fontFamily={SANS} fontSize="22" fontWeight="700"
      fill={color || '#78716c'} letterSpacing="1.5">
      {icon} {text.toUpperCase()}
    </text>
  );
}
function Lines({ x, startY, lines, size, lineH, weight = 400, family = SANS, fill = '#292524', style }) {
  return lines.map((l, i) => (
    <text key={i} x={x} y={startY + i * lineH + size} fontFamily={family} fontSize={size}
      fontWeight={weight} fontStyle={style} fill={fill}>{l}</text>
  ));
}
function CoverImage({ x, y, w, h, uri, idBase }) {
  const clipId = `clip-${idBase}`;
  if (!uri) {
    return <rect x={x} y={y} width={w} height={h} rx={6} fill="#f5f0e8" />;
  }
  return (
    <>
      <clipPath id={clipId}><rect x={x} y={y} width={w} height={h} rx={6} /></clipPath>
      <image href={uri} x={x} y={y} width={w} height={h} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
    </>
  );
}

// Book covers are portrait (~2:3 width:height). Dividing a full block width
// evenly across 4-5 covers produces a landscape box, which causes
// preserveAspectRatio="slice" to crop heavily into the cover art. Instead,
// derive width from a fixed cover height at the correct ratio, and center
// the row within the available block width.
function centeredCoverRow(count, coverH, blockWidth, gap = 10) {
  const coverW = coverH * (2 / 3);
  const totalW = count * coverW + (count - 1) * gap;
  const startX = BPAD + Math.max(0, (blockWidth - BPAD * 2 - totalW) / 2);
  return { coverW, gap, startX };
}

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
function renderHeader({ profile, imageMap, width }) {
  const AV     = 128;
  const textX  = PAD + AV + 28;
  const textW  = width - textX - PAD;

  const usernameLine = wrapText(`@${profile?.username || 'yourprofile'}`, textW, 40, 800, SERIF, 1)[0] || '';

  const metaParts = [];
  if (profile?.archetype_emoji || profile?.archetype_label) {
    metaParts.push(`${profile?.archetype_emoji || ''} ${profile?.archetype_label || ''}`.trim());
  }
  if (profile?.location) metaParts.push(`📍 ${profile.location}`);
  const metaText  = metaParts.join('   ·   ');
  const metaLines = wrapText(metaText, textW, 24, 400, SANS, 2);

  const usernameY  = PAD + 46;
  const metaStartY = usernameY + 36;
  const metaLineH  = 32;
  const metaBottom = metaStartY + (metaLines.length - 1) * metaLineH;

  // Genre pills
  const tags  = (profile?.interest_tags || []).slice(0, 3);
  const pillH = 44;
  const pillY = metaLines.length ? metaBottom + 30 : metaStartY + 4;
  let px = textX;
  const pills = [];
  tags.forEach((g, i) => {
    const label = wrapText(g, 9999, 22, 600, SANS, 1)[0] || '';
    const w = textWidth(label, 22, 600, SANS) + 32;
    if (px + w <= textX + textW) {
      pills.push({ x: px, w, label, dark: i === 0 });
      px += w + 10;
    }
  });

  const headerContentH = Math.max(AV, (pillY + pillH) - PAD);
  const dividerY = PAD + headerContentH + 26;

  const urlText = `reanloforreaders.com/u/${profile?.username || 'yourprofile'}`;
  const urlLine = wrapText(urlText, width - PAD * 2, 24, 700, SERIF, 1)[0] || '';

  const height = Math.round(dividerY + 38 + 22);
  const avatarUri = imageMap[profile?.avatar_url];

  const node = (
    <g>
      <rect width={width} height={height} fill="#fff" />
      <clipPath id="avatarClip"><circle cx={PAD + AV / 2} cy={PAD + AV / 2} r={AV / 2} /></clipPath>
      {avatarUri
        ? <image href={avatarUri} x={PAD} y={PAD} width={AV} height={AV} clipPath="url(#avatarClip)" preserveAspectRatio="xMidYMid slice" />
        : <circle cx={PAD + AV / 2} cy={PAD + AV / 2} r={AV / 2} fill="#f5f5f4" />}
      <circle cx={PAD + AV / 2} cy={PAD + AV / 2} r={AV / 2 - 3} fill="none" stroke="#d4a84b" strokeWidth="6" />

      <text x={textX} y={usernameY} fontFamily={SERIF} fontSize="40" fontWeight="800" fill="#292524">{usernameLine}</text>

      {metaLines.map((line, i) => (
        <text key={i} x={textX} y={metaStartY + i * metaLineH} fontFamily={SANS} fontSize="24" fill="#78716c">{line}</text>
      ))}

      {pills.map((p, i) => (
        <g key={i}>
          <rect x={p.x} y={pillY} width={p.w} height={pillH} rx={pillH / 2} fill={p.dark ? '#292524' : '#f5f0e8'} />
          <text x={p.x + p.w / 2} y={pillY + pillH / 2 + 8} fontFamily={SANS} fontSize="22" fontWeight="600"
            fill={p.dark ? '#faf8f5' : '#92400e'} textAnchor="middle">{p.label}</text>
        </g>
      ))}

      <line x1={0} y1={dividerY} x2={width} y2={dividerY} stroke="#e8e4dc" strokeWidth="2" />
      <text x={width / 2} y={dividerY + 38} fontFamily={SERIF} fontSize="24" fontWeight="700" fill="#B85C38" textAnchor="middle">{urlLine}</text>
    </g>
  );

  return { height, node };
}

// ─────────────────────────────────────────────────────────────────────────────
// Block renderers — each returns { height, node } or null if no content.
// node's coordinate origin is (0,0); the caller wraps in <g transform=translate>.
// ─────────────────────────────────────────────────────────────────────────────
function bCurrentThought({ profile, width }) {
  const text = activeThought(profile) || '';
  if (!text) return null;
  const innerW = width - BPAD * 2;
  const lines  = wrapText(`"${text}"`, innerW, 26, 400, SANS, 4);
  const h = BPAD * 2 + 28 + lines.length * 36;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fef9ec" stroke="#fde68a" />
      <Label icon="💭" text="Current Thought" color="#d97706" />
      <Lines x={BPAD} startY={BPAD + 32} lines={lines} size={26} lineH={36} family={SERIF} style="italic" fill="#292524" />
    </g>
  )};
}

// The share image shows the first book only, the same way it shows the
// first hot take, so the block stays one size however many there are.
function bCurrentlyReading({ profile, imageMap, width }) {
  const cr = firstCurrentlyReading(profile);
  if (!cr) return null;
  const cover = cr.cover_url || cr.cover_image_url;
  const coverW = 96, coverH = 138;
  const textX  = BPAD + coverW + 24;
  const textW  = width - textX - BPAD;
  const titleLines = wrapText(cr.title, textW, 28, 700, SERIF, 2);
  const authorLine = wrapText(cr.author || '', textW, 24, 400, SANS, 1)[0] || '';
  const reactionLines = cr.reaction ? wrapText(cr.reaction, textW, 22, 400, SANS, 2) : [];

  const labelH   = BPAD + 22 + 18;
  const textH    = titleLines.length * 34 + 10 + 28 + (reactionLines.length ? 10 + reactionLines.length * 28 : 0);
  const contentH = Math.max(coverH, textH);
  const h = labelH + contentH + BPAD;

  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="📖" text="Currently Reading" />
      <CoverImage x={BPAD} y={labelH} w={coverW} h={coverH} uri={imageMap[cover]} idBase="cr" />
      <Lines x={textX} startY={labelH} lines={titleLines} size={28} lineH={34} weight={700} family={SERIF} />
      <Lines x={textX} startY={labelH + titleLines.length * 34 + 10} lines={[authorLine]} size={24} lineH={28} fill="#78716c" />
      {reactionLines.length > 0 && (
        <Lines x={textX} startY={labelH + titleLines.length * 34 + 10 + 28 + 10} lines={reactionLines} size={22} lineH={28} style="italic" fill="#a8a29e" />
      )}
    </g>
  )};
}

function bToKnowMe({ profile, imageMap, width }) {
  const books = (profile?.to_know_me || []).filter(b => b?.title).slice(0, 3);
  if (!books.length) return null;
  const labelH = BPAD + 22 + 18;
  const coverH = 130;
  const { coverW, gap, startX } = centeredCoverRow(books.length, coverH, width);
  const h = labelH + coverH + BPAD;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="💛" text="Books That Made Me" />
      {books.map((b, i) => {
        const cover = b.cover_url || b.cover_image_url;
        const x = startX + i * (coverW + gap);
        return <CoverImage key={i} x={x} y={labelH} w={coverW} h={coverH} uri={imageMap[cover]} idBase={`tkm${i}`} />;
      })}
    </g>
  )};
}

// The share image shows slide 1 only, the same take the community feed
// shows (decided 8 Oct), so the block stays one size however many there are.
function bHotTake({ profile, width }) {
  const text = firstHotTake(profile);
  if (!text) return null;
  const innerW = width - BPAD * 2;
  const lines  = wrapText(text, innerW, 30, 700, SERIF, 3);
  const h = BPAD * 2 + 25 + lines.length * 40;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#1c1917" stroke="#292524" />
      <Label icon="🔥" text="Hot Take" color="#d97706" />
      <Lines x={BPAD} startY={BPAD + 28} lines={lines} size={30} lineH={40} weight={700} family={SERIF} fill="#faf8f5" />
    </g>
  )};
}

function bTopTropes({ profile, width }) {
  const tropes = (profile?.top_tropes || []).slice(0, 6);
  if (!tropes.length) return null;
  const innerW = width - BPAD * 2;
  const labelH = BPAD + 22 + 18;
  const pillH  = 44, gap = 8;

  // Wrap pills into rows. Any single label wider than the row itself is
  // truncated to fit, so a long custom trope name can't push its pill
  // past the right edge of the card.
  const rows = [[]];
  let rowW = 0;
  tropes.forEach(t => {
    let label = t;
    let tw = textWidth(label, 22, 600, SANS) + 32;
    if (tw > innerW) {
      label = wrapText(t, innerW - 32, 22, 600, SANS, 1)[0] || t;
      tw = textWidth(label, 22, 600, SANS) + 32;
    }
    if (rowW + tw > innerW && rows[rows.length - 1].length > 0) {
      rows.push([]);
      rowW = 0;
    }
    rows[rows.length - 1].push({ label, w: tw });
    rowW += tw + gap;
  });

  const h = labelH + rows.length * (pillH + gap) - gap + BPAD;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="🏷️" text="My Top Tropes" />
      {rows.map((row, ri) => {
        let x = BPAD;
        const y = labelH + ri * (pillH + gap);
        return row.map((p, pi) => {
          const px = x;
          x += p.w + gap;
          return (
            <g key={`${ri}-${pi}`}>
              <rect x={px} y={y} width={p.w} height={pillH} rx={pillH / 2} fill="#fef3c7" stroke="#fde68a" strokeWidth="2" />
              <text x={px + p.w / 2} y={y + pillH / 2 + 8} fontFamily={SANS} fontSize="22" fontWeight="600" fill="#92400e" textAnchor="middle">{p.label}</text>
            </g>
          );
        });
      })}
    </g>
  )};
}

function bReadingGoal({ width }) {
  const labelH = BPAD + 22 + 22;
  const barH = 14;
  const h = labelH + barH + 36 + BPAD;
  const innerW = width - BPAD * 2;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="🎯" text="Reading Goal" />
      <rect x={BPAD} y={labelH} width={innerW} height={barH} rx={barH / 2} fill="#e8e4dc" />
      <rect x={BPAD} y={labelH} width={innerW * 0.6} height={barH} rx={barH / 2} fill="#2D4A3E" />
      <text x={BPAD} y={labelH + barH + 30} fontFamily={SANS} fontSize="22" fill="#78716c">Tracking progress this year</text>
    </g>
  )};
}

function bReadingStats({ extra, width }) {
  const stats = extra?.readingStats;
  if (!stats?.books_read) return null;

  const labelH  = BPAD + 22 + 22;
  const cardGap = 12;
  const cardW   = (width - BPAD * 2 - cardGap) / 2;
  const cardH   = 90;
  const items = [
    { label: 'Books Read', value: stats.books_read || 0 },
    { label: 'Avg Rating', value: stats.avg_rating ? Number(stats.avg_rating).toFixed(1) : '—' },
    { label: 'Pages',      value: stats.total_pages ? (stats.total_pages >= 1000 ? `${Math.round(stats.total_pages / 1000)}k` : stats.total_pages) : '—' },
    { label: 'Authors',    value: stats.unique_authors || '—' },
  ];
  const h = labelH + cardH * 2 + cardGap + BPAD;

  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#1c1917" stroke="#292524" />
      <Label icon="📊" text="Reading Stats" color="#d97706" />
      {items.map((it, i) => {
        const col = i % 2, row = Math.floor(i / 2);
        const x = BPAD + col * (cardW + cardGap);
        const y = labelH + row * (cardH + cardGap);
        return (
          <g key={i}>
            <rect x={x} y={y} width={cardW} height={cardH} rx={10} fill="rgba(255,255,255,0.05)" />
            <text x={x + cardW / 2} y={y + cardH / 2 - 2} textAnchor="middle" fontFamily={SANS} fontSize="34" fontWeight="800" fill="#fff">{it.value}</text>
            <text x={x + cardW / 2} y={y + cardH / 2 + 24} textAnchor="middle" fontFamily={SANS} fontSize="18" fill="#78716c">{it.label}</text>
          </g>
        );
      })}
    </g>
  )};
}

function bRecommendMe({ profile, imageMap, width }) {
  const recs = (profile?.recommend_me || []).slice(0, 3);
  if (!recs.length) return null;
  const labelH = BPAD + 22 + 18;
  const coverH = 130;
  const { coverW, gap, startX } = centeredCoverRow(recs.length, coverH, width);
  const pitch = recs[0]?.pitch;
  const innerW = width - BPAD * 2;
  const pitchLines = pitch ? wrapText(`"${pitch}"`, innerW, 24, 400, SANS, 2) : [];
  const h = labelH + coverH + (pitchLines.length ? 16 + pitchLines.length * 30 : 0) + BPAD;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="📚" text="Recommend Me" />
      {recs.map((r, i) => {
        const cover = r.cover_url || r.cover_image_url;
        const x = startX + i * (coverW + gap);
        return <CoverImage key={i} x={x} y={labelH} w={coverW} h={coverH} uri={imageMap[cover]} idBase={`rec${i}`} />;
      })}
      {pitchLines.length > 0 && (
        <Lines x={BPAD} startY={labelH + coverH + 16} lines={pitchLines} size={24} lineH={30} style="italic" fill="#78716c" />
      )}
    </g>
  )};
}

function bSubstack({ profile, extra, width }) {
  const newsletter = parseNewsletterUrl(profile?.substack_url);
  if (!newsletter) return null;
  const url = newsletter.stored;
  const posts = extra?.substackPosts || [];
  const labelH = BPAD + 22 + 18;

  if (posts.length === 0) {
    const noPostLine = wrapText(`No posts found for ${url}`, width - BPAD * 2, 22, 400, SANS, 1)[0] || '';
    const h = labelH + 30 + BPAD;
    return { height: h, node: (
      <g>
        <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
        <Label icon="✍️" text={newsletter.platform} />
        <text x={BPAD} y={labelH + 22} fontFamily={SANS} fontSize="22" fill="#a8a29e">{noPostLine}</text>
      </g>
    )};
  }

  const post = posts[0];
  const innerW = width - BPAD * 2;
  const titleLines = wrapText(post.title || 'Untitled post', innerW, 26, 700, SANS, 2);
  const dateLine = post.pubDate ? new Date(post.pubDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const postLink = post.link || post.guid || url;
  const linkLine = wrapText(postLink, innerW, 20, 400, SANS, 1)[0] || '';
  const h = labelH + titleLines.length * 32 + 10 + 28 + 28 + BPAD - 8;

  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="✍️" text={`Latest from ${newsletter.platform}`} />
      <Lines x={BPAD} startY={labelH} lines={titleLines} size={26} lineH={32} weight={700} fill="#292524" />
      <text x={BPAD} y={labelH + titleLines.length * 32 + 10 + 22} fontFamily={SANS} fontSize="20" fill="#a8a29e">{dateLine}</text>
      <text x={BPAD} y={labelH + titleLines.length * 32 + 10 + 22 + 28} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#B85C38">{linkLine}</text>
    </g>
  )};
}

function bYoutube({ profile, imageMap, width }) {
  const videos = profile?.youtube_data?.videos || [];
  if (!videos.length) return null;
  const first = videos[0];
  const labelH = BPAD + 22 + 18;
  // Scale thumb down for narrow blocks (e.g. paired side-by-side)
  const thumbW = Math.min(200, Math.floor(width * 0.38));
  const thumbH = Math.round(thumbW * 9 / 16);
  const textX  = BPAD + thumbW + 20;
  const textW  = width - textX - BPAD;
  const titleLines = wrapText(first.title || 'YouTube Video', textW, 24, 400, SANS, 3);
  const extraLine  = videos.length > 1 ? wrapText(`+${videos.length - 1} more video${videos.length > 2 ? 's' : ''}`, textW, 20, 400, SANS, 1) : [];
  const videoUrl = first.url || first.video_url || (first.video_id ? `https://youtu.be/${first.video_id}` : '');
  const channelUrl = profile?.youtube_data?.channel_url || videoUrl;
  const linkLine = wrapText(channelUrl, textW, 20, 600, SANS, 1)[0] || '';
  const contentH = Math.max(thumbH, titleLines.length * 30 + (extraLine.length ? 30 : 0) + (linkLine ? 28 : 0));
  const h = labelH + contentH + BPAD;
  const clipId = 'yt-text-clip';
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="🎬" text="My YouTube" />
      <CoverImage x={BPAD} y={labelH} w={thumbW} h={thumbH} uri={imageMap[first.thumbnail_url]} idBase="yt" />
      {/* Clip text so it can never overflow the block right edge */}
      <clipPath id={clipId}>
        <rect x={textX} y={labelH} width={textW} height={contentH} />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <Lines x={textX} startY={labelH} lines={titleLines} size={24} lineH={30} fill="#292524" />
        {extraLine.length > 0 && (
          <Lines x={textX} startY={labelH + titleLines.length * 30 + 6} lines={extraLine} size={20} lineH={26} fill="#a8a29e" />
        )}
        {linkLine ? <text x={textX} y={labelH + titleLines.length * 30 + (extraLine.length ? 34 : 8) + 20} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#B85C38">{linkLine}</text> : null}
      </g>
    </g>
  )};
}

function bPodcast({ profile, width }) {
  const eps  = profile?.podcast_data?.episodes || [];
  if (!eps.length) return null;
  const name = profile?.podcast_data?.podcast_name || 'My Podcast';
  const labelH = BPAD + 22 + 18;
  const innerW = width - BPAD * 2;
  const nameLine = wrapText(name, innerW, 28, 700, SANS, 1)[0] || '';
  const epLine = wrapText(eps[0]?.title || '', innerW, 22, 400, SANS, 1)[0] || '';
  const epUrl = eps[0]?.link || eps[0]?.url || profile?.podcast_data?.podcast_url || '';
  const epUrlLine = epUrl ? wrapText(epUrl, innerW, 20, 600, SANS, 1)[0] || '' : '';
  const h = labelH + 34 + 30 + 28 + (epUrlLine ? 28 : 0) + BPAD;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="🎙️" text="My Podcast" />
      <text x={BPAD} y={labelH + 26} fontFamily={SANS} fontSize="28" fontWeight="700" fill="#292524">{nameLine}</text>
      <text x={BPAD} y={labelH + 26 + 32} fontFamily={SANS} fontSize="22" fill="#78716c">{epLine}</text>
      <text x={BPAD} y={labelH + 26 + 32 + 28} fontFamily={SANS} fontSize="20" fill="#a8a29e">{eps.length} episode{eps.length !== 1 ? 's' : ''}</text>
      {epUrlLine ? <text x={BPAD} y={labelH + 26 + 32 + 28 + 28} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#B85C38">{epUrlLine}</text> : null}
    </g>
  )};
}

function bAskMeAbout({ profile, width }) {
  const services = (profile?.ask_me_about?.services || []).slice(0, 4);
  if (!services.length) return null;
  const labelH = BPAD + 22 + 18;
  const lineH = 32;
  const innerW = width - BPAD * 2;
  const email = profile?.ask_me_about?.email || profile?.email || '';
  const mailtoLine = email ? wrapText(`mailto:${email}`, innerW, 20, 600, SANS, 1)[0] || '' : '';
  const h = labelH + services.length * lineH + (mailtoLine ? 32 : 0) + BPAD - 6;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#1a3a2e" stroke="#1e4035" />
      <Label icon="💼" text="Ask Me About" color="#6ee7b7" />
      {services.map((s, i) => {
        const line = wrapText(s.name || '', innerW, 24, 500, SANS, 1)[0] || '';
        return <text key={i} x={BPAD} y={labelH + i * lineH + 22} fontFamily={SANS} fontSize="24" fontWeight="500" fill="#d1fae5">{line}</text>;
      })}
      {mailtoLine ? <text x={BPAD} y={labelH + services.length * lineH + 22} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#6ee7b7">{mailtoLine}</text> : null}
    </g>
  )};
}

function bMyShop({ profile, extra, imageMap, width }) {
  const products = extra?.shopProducts || [];
  const url = profile?.shop_url;

  if (products.length > 0) {
    const labelH  = BPAD + 22 + 18;
    const items   = products.slice(0, 3);
    const gap     = 16;
    const colW    = (width - BPAD * 2 - gap * (items.length - 1)) / items.length;
    const imgSize = 100;
    // content rows: image + name (26px) + price (20px) = imgSize + 56
    // then 20px gap + link line + BPAD at bottom
    const contentH = imgSize + 56;
    const linkY = labelH + contentH + 20;
    const h = url ? linkY + 24 + BPAD : labelH + contentH + BPAD;
    return { height: h, node: (
      <g>
        <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
        <Label icon="🛍️" text="My Shop" />
        {items.map((p, i) => {
          const x = BPAD + i * (colW + gap);
          const nameLines = wrapText(p.name || 'Product', colW, 22, 600, SANS, 1);
          const priceRaw = p.price ? (String(p.price).startsWith('$') ? p.price : `$${p.price}`) : '';
          const price = wrapText(priceRaw, colW, 20, 700, SANS, 1)[0] || '';
          const uri = imageMap[p.image_url];
          return (
            <g key={i}>
              {uri
                ? <CoverImage x={x} y={labelH} w={imgSize} h={imgSize} uri={uri} idBase={`shop${i}`} />
                : (
                  <>
                    <rect x={x} y={labelH} width={imgSize} height={imgSize} rx={10} fill="#f5f0e8" />
                    <text x={x + imgSize / 2} y={labelH + imgSize / 2 + 14} textAnchor="middle" fontSize="40">{p.emoji || '📦'}</text>
                  </>
                )}
              <Lines x={x} startY={labelH + imgSize + 8} lines={nameLines} size={22} lineH={26} weight={600} fill="#292524" />
              {price && <text x={x} y={labelH + imgSize + 54} fontFamily={SANS} fontSize="20" fontWeight="700" fill="#d97706">{price}</text>}
            </g>
          );
        })}
        {url && <text x={BPAD} y={linkY + 20} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#B85C38">{wrapText(url, width - BPAD * 2, 20, 600, SANS, 1)[0] || ''}</text>}
      </g>
    )};
  }

  if (!url) return null;

  const innerW = width - BPAD * 2 - 60;
  const urlLine = wrapText(url, innerW, 22, 400, SANS, 1)[0] || '';
  const h = BPAD * 2 + 60;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <text x={BPAD} y={BPAD + 36} fontSize="40">🛍️</text>
      <text x={BPAD + 56} y={BPAD + 24} fontFamily={SANS} fontSize="26" fontWeight="700" fill="#292524">My Shop</text>
      <text x={BPAD + 56} y={BPAD + 50} fontFamily={SANS} fontSize="22" fill="#78716c">{urlLine}</text>
    </g>
  )};
}

function bAmazonStorefront({ profile, width }) {
  const lists = (profile?.amazon_storefront?.lists || []).filter(l => l.enabled).slice(0, 3);
  if (!lists.length) return null;
  const headerH = 56;
  const lineH = 38;
  const innerW = width - BPAD * 2;
  const h = headerH + lists.length * lineH + 16;
  const displayName = wrapText(profile?.amazon_storefront?.display_name || 'My Storefront', innerW - 60, 24, 700, SANS, 1)[0] || '';
  const storefrontUrl = profile?.amazon_storefront?.url || profile?.amazon_storefront?.storefront_url || '';
  const storefrontLink = storefrontUrl ? wrapText(storefrontUrl, innerW, 20, 600, SANS, 1)[0] || '' : '';
  const hWithLink = storefrontLink ? h + 30 : h;
  return { height: hWithLink, node: (
    <g>
      <Frame w={width} h={hWithLink} fill="#fff" stroke="#e8e4dc" />
      <rect width={width} height={headerH} rx={RADIUS} fill="#232f3e" />
      <rect y={headerH - RADIUS} width={width} height={RADIUS} fill="#232f3e" />
      <text x={BPAD} y={headerH / 2 + 8} fontFamily={SANS} fontSize="24" fontWeight="700" fill="#faf8f5">📦 {displayName}</text>
      {lists.map((l, i) => {
        const label = wrapText(`${l.icon} ${l.name}`, innerW, 24, 600, SANS, 1)[0] || '';
        return <text key={i} x={BPAD} y={headerH + 16 + i * lineH + 22} fontFamily={SANS} fontSize="24" fontWeight="600" fill="#292524">{label}</text>;
      })}
      {storefrontLink ? <text x={BPAD} y={hWithLink - 12} fontFamily={SANS} fontSize="20" fontWeight="600" fill="#B85C38">{storefrontLink}</text> : null}
    </g>
  )};
}

function bShowcase({ profile, imageMap, width }) {
  const items = (profile?.showcase_items || []).slice(0, 2);
  if (!items.length) return null;
  const labelH = BPAD + 22 + 18;
  const gap = 10;
  const itemW = (width - BPAD * 2 - gap * (items.length - 1)) / items.length;
  const itemH = itemW * 9 / 16;
  const h = labelH + itemH + BPAD;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="🎨" text="Showcase" />
      {items.map((it, i) => {
        const x = BPAD + i * (itemW + gap);
        return <CoverImage key={i} x={x} y={labelH} w={itemW} h={itemH} uri={imageMap[it.url]} idBase={`sc${i}`} />;
      })}
    </g>
  )};
}

function bEvents({ extra, width }) {
  const events = (extra?.events || []).slice(0, 3);
  const labelH = BPAD + 22 + 18;

  if (events.length === 0) {
    const h = labelH + 30 + BPAD;
    return { height: h, node: (
      <g>
        <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
        <Label icon="📍" text="My Events" />
        <text x={BPAD} y={labelH + 22} fontFamily={SANS} fontSize="22" fill="#a8a29e">No upcoming events yet</text>
      </g>
    )};
  }

  const rowH = 56;
  const h = labelH + events.length * rowH + BPAD - 8;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="📍" text="My Events" />
      {events.map((ev, i) => {
        const y = labelH + i * rowH;
        const dateStr  = ev.date ? new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
        const subtitle = [dateStr, ev.venue].filter(Boolean).join(' · ');
        const innerW   = width - BPAD * 2 - 56;
        const nameLine = wrapText(ev.name || 'Event', innerW, 24, 700, SANS, 1)[0] || '';
        const subLine  = wrapText(subtitle, innerW, 20, 400, SANS, 1)[0] || '';
        return (
          <g key={i}>
            <rect x={BPAD} y={y} width={40} height={40} rx={10} fill={ev.color || '#2D4A3E'} />
            <text x={BPAD + 20} y={y + 27} textAnchor="middle" fontSize="20">{ev.emoji || '📍'}</text>
            <text x={BPAD + 52} y={y + 19} fontFamily={SANS} fontSize="24" fontWeight="700" fill="#292524">{nameLine}</text>
            {subLine && <text x={BPAD + 52} y={y + 41} fontFamily={SANS} fontSize="20" fill="#78716c">{subLine}</text>}
          </g>
        );
      })}
    </g>
  )};
}

function bCustomShelf({ blocks, imageMap, width }) {
  const block = (blocks || []).find(b => b.type === 'custom_shelf');
  const books = (block?.data?.books || []).slice(0, 4);
  if (!books.length) return null;
  const name  = block?.data?.shelf_name  || 'My Shelf';
  const emoji = block?.data?.shelf_emoji || '📚';
  const titleH = 50;
  const coverH = 130;
  const { coverW, gap, startX } = centeredCoverRow(books.length, coverH, width);
  const h = BPAD + titleH + coverH + BPAD;
  const title = wrapText(`${emoji} ${name}`, width - BPAD * 2, 28, 700, SANS, 1)[0] || '';
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <text x={BPAD} y={BPAD + 28} fontFamily={SANS} fontSize="28" fontWeight="700" fill="#292524">{title}</text>
      {books.map((b, i) => {
        const cover = b.cover_url || b.cover_image_url;
        const x = startX + i * (coverW + gap);
        return <CoverImage key={i} x={x} y={BPAD + titleH} w={coverW} h={coverH} uri={imageMap[cover]} idBase={`cs${i}`} />;
      })}
    </g>
  )};
}

function bJournal({ extra, imageMap, width }) {
  const entry = extra?.journalEntry;
  if (!entry?.reflection_text?.trim()) return null;
  const cover  = entry.book?.cover_image_url;
  const coverW = 96, coverH = 138;
  const textX  = BPAD + coverW + 24;
  const textW  = width - textX - BPAD;
  const labelH = BPAD + 22 + 18;

  const kicker = entry.prompt_kind === 'talk'
    ? '💬 talked about it'
    : `${entry.prompt_emoji || '📓'} ${entry.prompt_label || 'A Book That...'}`;
  const titleLines = wrapText(entry.book?.title || 'a book', textW, 26, 700, SERIF, 2);
  const quoteLines = wrapText(`"${entry.reflection_text.trim()}"`, textW, 23, 400, SANS, 3);

  const textH    = 20 + titleLines.length * 30 + 10 + quoteLines.length * 29;
  const contentH = Math.max(coverH, textH);
  const h = labelH + contentH + BPAD;

  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <Label icon="📓" text="A Book That..." />
      <CoverImage x={BPAD} y={labelH} w={coverW} h={coverH} uri={imageMap[cover]} idBase="jr" />
      <text x={textX} y={labelH + 20} fontFamily={SANS} fontSize="22" fontWeight="700" fill="#B85C38">{kicker}</text>
      <Lines x={textX} startY={labelH + 26} lines={titleLines} size={26} lineH={30} weight={700} family={SERIF} />
      <Lines x={textX} startY={labelH + 26 + titleLines.length * 30 + 10} lines={quoteLines} size={23} lineH={29} style="italic" fill="#57534e" />
    </g>
  )};
}

function bDefault({ blockType, width }) {
  const meta = BLOCK_CATALOGUE.find(b => b.id === blockType);
  const h = BPAD * 2 + 40;
  return { height: h, node: (
    <g>
      <Frame w={width} h={h} fill="#fff" stroke="#e8e4dc" />
      <text x={BPAD} y={BPAD + 32} fontSize="40">{meta?.icon || '📦'}</text>
      <text x={BPAD + 56} y={BPAD + 32} fontFamily={SANS} fontSize="26" fontWeight="700" fill="#292524">{meta?.name || blockType}</text>
    </g>
  )};
}

const RENDERERS = {
  current_thought:   bCurrentThought,
  currently_reading: bCurrentlyReading,
  to_know_me:        bToKnowMe,
  hot_take:          bHotTake,
  top_tropes:        bTopTropes,
  reading_goal:      bReadingGoal,
  reading_stats:     bReadingStats,
  recommend_me:      bRecommendMe,
  substack:          bSubstack,
  youtube:           bYoutube,
  podcast:           bPodcast,
  ask_me_about:      bAskMeAbout,
  my_shop:           bMyShop,
  amazon_storefront: bAmazonStorefront,
  showcase:          bShowcase,
  events:            bEvents,
  custom_shelf:      bCustomShelf,
  journal:           bJournal,
};

function renderBlock(blockType, props) {
  const fn = RENDERERS[blockType] || ((p) => bDefault({ ...p, blockType }));
  return fn(props);
}

// Pair small blocks (weight ≤ 2) side by side
function computeRows(includedIds) {
  const rows = [];
  let i = 0;
  while (i < includedIds.length) {
    const a  = includedIds[i];
    const b  = includedIds[i + 1];
    const wA = BLOCK_CATALOGUE.find(x => x.id === a)?.wt || 3;
    const wB = b ? (BLOCK_CATALOGUE.find(x => x.id === b)?.wt || 3) : 99;
    if (b && wA <= 2 && wB <= 2) { rows.push([a, b]); i += 2; }
    else                          { rows.push([a]);     i += 1; }
  }
  return rows;
}

// ─────────────────────────────────────────────────────────────────────────────
// Full card layout — returns SVG children + total height used
// ─────────────────────────────────────────────────────────────────────────────
function buildCard({ profile, blocks, included, imageMap, extra, formatH }) {
  const header = renderHeader({ profile, imageMap, width: CARD_W });
  const rows = computeRows(included);

  let y = header.height + 20;
  const nodes = [<g key="header">{header.node}</g>];

  if (included.length === 0) {
    const h = 200;
    nodes.push(
      <g key="empty" transform={`translate(${PAD},${y})`}>
        <rect width={CARD_W - PAD * 2} height={h} rx={RADIUS} fill="none" stroke="#e8e4dc" strokeWidth="3" strokeDasharray="10,8" />
        <text x={(CARD_W - PAD * 2) / 2} y={h / 2 + 8} fontFamily={SANS} fontSize="26" fill="#a8a29e" textAnchor="middle">Add blocks to fill your card</text>
      </g>
    );
    y += h;
  } else {
    rows.forEach((row, ri) => {
      if (row.length === 2) {
        const colW = (CARD_W - PAD * 2 - GAP) / 2;
        const ra = renderBlock(row[0], { profile, blocks, imageMap, extra, width: colW });
        const rb = renderBlock(row[1], { profile, blocks, imageMap, extra, width: colW });
        if (!ra && !rb) return;
        const rowH = Math.max(ra?.height || 0, rb?.height || 0);
        if (ra) nodes.push(<g key={`r${ri}a`} transform={`translate(${PAD},${y})`}>{ra.node}</g>);
        if (rb) nodes.push(<g key={`r${ri}b`} transform={`translate(${PAD + colW + GAP},${y})`}>{rb.node}</g>);
        y += rowH + GAP;
      } else {
        const r = renderBlock(row[0], { profile, blocks, imageMap, extra, width: CARD_W - PAD * 2 });
        if (!r) return;
        nodes.push(<g key={`r${ri}`} transform={`translate(${PAD},${y})`}>{r.node}</g>);
        y += r.height + GAP;
      }
    });
  }

  // Watermark
  nodes.push(
    <text key="watermark" x={CARD_W / 2} y={formatH - 28} fontFamily={SERIF} fontSize="20" fill="#c4b8a8" textAnchor="middle" letterSpacing="0.5">
      made with reanlo · your reading life, shared
    </text>
  );

  return nodes;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composer
// ─────────────────────────────────────────────────────────────────────────────
function ShareComposer({ profile, blocks, onClose, isMobile, username, profileUrl }) {
  const allBlocks = blocks || [];

  const [fmt,      setFmt]      = useState('story');
  const [extra,        setExtra]        = useState({ readingStats: null, events: [], substackPosts: [], shopProducts: [], journalEntry: null });
  const [extraLoaded,  setExtraLoaded]  = useState(false);
  const [included, setIncluded] = useState(() => {
    const activeTypes = allBlocks.map(b => b.type);
    // journal excluded here on purpose -- see the BLOCK_CATALOGUE comment
    // above the 'journal' entry. Everything else keeps picking the first 3
    // catalogue-order blocks with real content, same as before.
    return BLOCK_CATALOGUE
      .filter(b => b.id !== 'journal' && activeTypes.includes(b.id) && blockHasContent(b.id, profile, allBlocks, {}))
      .slice(0, 3)
      .map(b => b.id);
  });
  const [imageMap,    setImageMap]    = useState({});
  const [imagesReady, setImagesReady] = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [done,        setDone]        = useState(false);
  const svgRef = useRef(null);

  const f = FORMATS[fmt];

  // Fetch data that lives outside `profile`: reading stats, upcoming events, latest Substack post
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = { readingStats: null, events: [], substackPosts: [], shopProducts: [] };
      try {
        const user = await getCurrentUser();
        const uid = profile?.user_id || user?.id;
        if (uid) {
          const { data: csv } = await supabase.from('user_csv_stats').select('*').eq('user_id', uid).maybeSingle();
          if (csv) {
            result.readingStats = csv;
          } else {
            const { data: lib } = await supabase
              .from('user_library')
              .select('rating, books ( author_name, page_count )')
              .eq('user_id', uid)
              .in('shelf', ['read', 'loved']);
            if (lib?.length) {
              const rated      = lib.filter(b => b.rating);
              // null, not 0 — no rated books means no average. Same luck-not-
              // intent situation as ProfileBlocks: 0 is falsy so the '—' branch
              // already fired, but the value itself was a lie.
              const avgRating  = rated.length ? (rated.reduce((s, b) => s + b.rating, 0) / rated.length).toFixed(1) : null;
              const totalPages = lib.reduce((s, b) => s + (b.books?.page_count || 250), 0);
              const uniqueAuthors = new Set(lib.map(b => b.books?.author_name).filter(Boolean)).size;
              result.readingStats = { books_read: lib.length, avg_rating: avgRating, total_pages: totalPages, unique_authors: uniqueAuthors, source: 'library' };
            }
          }

          const { data: events } = await onlyUpcoming(supabase
            .from('profile_events')
            .select('*')
            .eq('user_id', uid))
            .order('date', { ascending: true });
          result.events = events || [];

          const { data: shopProducts } = await supabase
            .from('shop_products')
            .select('*')
            .eq('user_id', uid)
            .order('created_at', { ascending: true });
          result.shopProducts = shopProducts || [];

          // Latest SHARED reflection only -- per review on this PR: the whole
          // journal system was built so reflection_text never leaves the
          // reader's own view unless they explicitly marked the entry shared
          // (shared_reflections_public enforces the same line at the schema
          // level, and doesn't even carry reflection_text as a column). A
          // download card is exactly the public side of that line, so this
          // has to filter the same way rather than reading every entry
          // regardless of visibility, which is what the first version of
          // this did. A reader who wants an entry on the card already has a
          // way to say so -- marking it shared -- and that tap is what
          // should carry the meaning, not this toggle.
          const { data: reflectionRows } = await supabase
            .from('reflections')
            .select('*')
            .eq('reader_id', uid)
            .eq('visibility', 'shared')
            // A card names the book, so an entry with no book is passed over (it is never shared anyway).
            .not('book_id', 'is', null)
            .order('created_at', { ascending: false })
            .limit(1);
          const latest = reflectionRows?.[0];
          if (latest?.reflection_text?.trim()) {
            const { data: book } = await supabase
              .from('books')
              .select('title, author_name, cover_image_url')
              .eq('id', latest.book_id)
              .maybeSingle();
            result.journalEntry = { ...latest, book: book || null };
          }
        }
      } catch (err) {
        console.error('Share card data fetch error:', err.message);
      }

      // Feed URL comes from the shared parser. This used to take everything
      // before the first dot and glue `.substack.com` onto it, so a Medium
      // address resolved to `medium.substack.com` and an email to nonsense.
      const newsletter = parseNewsletterUrl(profile?.substack_url);
      if (newsletter) {
        try {
          const res  = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(newsletter.feed)}`);
          const data = await res.json();
          if (data.status === 'ok') result.substackPosts = (data.items || []).slice(0, 3);
        } catch (err) {
          console.warn('Newsletter RSS error:', err.message);
        }
      }

      if (!cancelled) { setExtra(result); setExtraLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [profile?.user_id, profile?.substack_url]);

  // Fetch & cache images as data URIs whenever the needed set changes
  const neededUrls = useMemo(() => collectImageUrls(profile, allBlocks, included, extra), [profile, allBlocks, included, extra]);

  useEffect(() => {
    let cancelled = false;
    const missing = neededUrls.filter(u => !(u in imageMap));
    if (missing.length === 0) { setImagesReady(true); return; }
    setImagesReady(false);
    (async () => {
      const entries = await Promise.all(missing.map(async u => [u, await toDataUri(u)]));
      if (cancelled) return;
      setImageMap(prev => {
        const next = { ...prev };
        entries.forEach(([u, d]) => { next[u] = d; });
        return next;
      });
      setImagesReady(true);
    })();
    return () => { cancelled = true; };
  }, [neededUrls.join('|')]);

  const cap       = f.cap;
  const used      = included.reduce((s, id) => s + (BLOCK_CATALOGUE.find(b => b.id === id)?.wt || 0), 0);
  const remaining = cap - used;
  const fillPct   = Math.min(100, (used / cap) * 100);
  const meterColor = used > cap ? '#dc2626' : used > cap * 0.85 ? '#E8603C' : '#2D4A3E';

  const activeTypes   = allBlocks.map(b => b.type);
  const showCatalogue = BLOCK_CATALOGUE.filter(b => activeTypes.includes(b.id) || blockHasContent(b.id, profile, allBlocks, extra));

  function handleFmtChange(k) {
    setFmt(k);
    const newCap = FORMATS[k].cap;
    let runWt = 0;
    const trimmed = [];
    for (const id of included) {
      const wt = BLOCK_CATALOGUE.find(b => b.id === id)?.wt || 0;
      if (runWt + wt <= newCap) { trimmed.push(id); runWt += wt; }
    }
    setIncluded(trimmed);
  }

  function toggle(id) {
    if (included.includes(id)) { setIncluded(included.filter(x => x !== id)); return; }
    const b = BLOCK_CATALOGUE.find(x => x.id === id);
    if (b && b.wt <= remaining) setIncluded([...included, id]);
  }

  function move(id, dir) {
    const i = included.indexOf(id), j = i + dir;
    if (j < 0 || j >= included.length) return;
    const arr = [...included];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setIncluded(arr);
  }

  const cardNodes = useMemo(() => {
    try {
      return buildCard({ profile, blocks: allBlocks, included, imageMap, extra, formatH: f.h });
    } catch (err) {
      console.error('Card build failed:', err);
      return [];
    }
  }, [profile, allBlocks, included, imageMap, extra, f.h]);

  async function handleDownload() {
    if (generating || !svgRef.current) return;
    setGenerating(true);
    try {
      const svgEl = svgRef.current;
      let svgStr = new XMLSerializer().serializeToString(svgEl);
      if (!svgStr.includes('xmlns=')) {
        svgStr = svgStr.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload  = resolve;
        img.onerror = reject;
        img.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width  = CARD_W;
      canvas.height = f.h;
      const c = canvas.getContext('2d');
      c.fillStyle = '#faf8f5';
      c.fillRect(0, 0, CARD_W, f.h);
      c.drawImage(img, 0, 0, CARD_W, f.h);
      URL.revokeObjectURL(url);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

      if (isMobile && navigator.canShare?.({ files: [new File([blob], 'x.png', { type: 'image/png' })] })) {
        await navigator.share({
          files: [new File([blob], `reanlo-${username}.png`, { type: 'image/png' })],
          title: `@${username} on Reanlo`,
          url: profileUrl,
        });
      } else {
        const dl = URL.createObjectURL(blob);
        Object.assign(document.createElement('a'), { href: dl, download: `reanlo-${username}.png` }).click();
        URL.revokeObjectURL(dl);
      }
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate image. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  // Preview sizing — SVG scales perfectly via viewBox, no transform tricks needed
  const PREVIEW_W = 260;
  const PREVIEW_H = Math.round(PREVIEW_W * (f.h / CARD_W));

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'linear-gradient(180deg,#FFF5EE,#FAF0E6)', overflowY: 'auto', fontFamily: 'var(--font-body)' }}>

      <div style={{ position: isMobile ? 'fixed' : 'sticky', top: 0, left: 0, right: 0, zIndex: 10, background: 'rgba(255,245,238,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #ece3d4', padding: isMobile ? '60px 20px 14px' : '62px 20px 14px', display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b7355', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}>‹ Back</button>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1.5, color: 'var(--brand-rust)', textTransform: 'uppercase', margin: 'var(--space-none)' }}>Share your passport</p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 17 : 21, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Build your share card</h1>
        </div>
      </div>

      <div style={{ padding: isMobile ? '120px 16px 40px' : '28px 32px 48px', maxWidth: 1060, margin: '0 auto' }}>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '0 0 24px' }}>Pick a size, choose which blocks to include, arrange the order, then download.</p>

        <div style={{ display: isMobile ? 'flex' : 'grid', flexDirection: isMobile ? 'column' : undefined, gridTemplateColumns: '1fr 280px', gap: 'var(--space-h3)', alignItems: 'start' }}>

          {/* Controls */}
          <div>
            <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: 'var(--neutral-800)', textTransform: 'uppercase', margin: '0 0 10px' }}>1 · Pick a size</p>
            <div style={{ display: 'flex', gap: 'var(--space-10)', marginBottom: 28 }}>
              {Object.entries(FORMATS).map(([k, v]) => (
                <button key={k} onClick={() => handleFmtChange(k)} style={{ flex: 1, padding: 'var(--space-14)', borderRadius: 14, border: fmt === k ? '2px solid var(--brand-rust)' : '2px solid var(--brand-sand)', background: fmt === k ? 'var(--brand-white)' : '#fdfbf7', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
                    <div style={{ width: k === 'story' ? 14 : 20, height: k === 'story' ? 24 : 20, border: `2px solid ${fmt === k ? '#B85C38' : '#d6d3d1'}`, borderRadius: 3 }} />
                    <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>{v.label}</span>
                  </div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: '#8b7355', margin: 'var(--space-none)' }}>{v.dims}</p>
                  <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '2px 0 0' }}>{k === 'story' ? 'IG / TikTok stories' : 'Feed post'}</p>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-sm)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: 'var(--neutral-800)', textTransform: 'uppercase', margin: 'var(--space-none)' }}>2 · Choose what fits</p>
              <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: meterColor }}>{remaining > 0 ? `room for ~${remaining} more` : 'card full'}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: '#ece3d4', marginBottom: 'var(--space-14)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${fillPct}%`, background: meterColor, borderRadius: 3, transition: 'width .25s, background .25s' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)', marginBottom: 28 }}>
              {showCatalogue.map(b => {
                const on      = included.includes(b.id);
                const blocked = !on && b.wt > remaining;
                const hasData = blockHasContent(b.id, profile, allBlocks, extra);
                return (
                  <button key={b.id} onClick={() => toggle(b.id)} disabled={blocked}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '10px 12px', borderRadius: 'var(--radius-lg)', border: on ? '2px solid var(--brand-forest)' : '1px solid var(--brand-sand)', background: on ? '#f3f7f5' : blocked ? '#f7f5f2' : 'var(--brand-white)', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.45 : 1, textAlign: 'left', transition: 'all 0.12s' }}>
                    <span style={{ fontSize: 'var(--font-size-md)' }}>{b.icon}</span>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-800)' }}>{b.name}</span>
                    {on && !hasData
                      ? <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--feedback-warning)', fontWeight: 700 }}>empty</span>
                      : <span style={{ fontSize: 'var(--font-size-base)', color: on ? 'var(--brand-forest)' : 'var(--neutral-300)', fontWeight: 700 }}>{on ? '✓' : '+'}</span>
                    }
                  </button>
                );
              })}
            </div>

            <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: 'var(--neutral-800)', textTransform: 'uppercase', margin: '0 0 10px' }}>3 · Arrange the order</p>
            <div style={{ background: '#fdfbf7', borderRadius: 'var(--radius-lg)', border: '1px solid #ece3d4', padding: 'var(--space-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '8px 10px', marginBottom: 'var(--space-xs)', background: 'var(--brand-forest)', borderRadius: 'var(--radius-md)' }}>
                <span style={{ fontSize: 'var(--font-size-base)' }}>👤</span>
                <span style={{ flex: 1, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--brand-white)' }}>Profile Header</span>
                <span style={{ fontSize: 'var(--font-size-micro)', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>ALWAYS ON · LOCKED</span>
              </div>
              {included.length === 0 && (
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', textAlign: 'center', padding: '12px 0', margin: 'var(--space-none)' }}>Add blocks above to arrange them here.</p>
              )}
              {included.map((id, i) => {
                const b = BLOCK_CATALOGUE.find(x => x.id === id);
                if (!b) return null;
                return (
                  <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '8px 10px', marginBottom: i < included.length - 1 ? 4 : 0, background: 'var(--brand-white)', borderRadius: 'var(--radius-md)', border: '1px solid #f0e9dd' }}>
                    <span style={{ fontSize: 'var(--font-size-base)' }}>{b.icon}</span>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-800)' }}>{b.name}</span>
                    <button onClick={() => move(id, -1)} disabled={i === 0} style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', fontSize: 'var(--font-size-sm)', color: i === 0 ? 'var(--brand-sand)' : '#8b7355', padding: '2px 4px' }}>▲</button>
                    <button onClick={() => move(id, 1)} disabled={i === included.length - 1} style={{ border: 'none', background: 'none', cursor: i === included.length - 1 ? 'default' : 'pointer', fontSize: 'var(--font-size-sm)', color: i === included.length - 1 ? 'var(--brand-sand)' : '#8b7355', padding: '2px 4px' }}>▼</button>
                    <button onClick={() => toggle(id)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: '#c4b8a8', padding: '2px 4px' }}>✕</button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Live preview — actual SVG, scaled via viewBox. What you see IS what exports. */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top: 80 }}>
            <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: 'var(--neutral-800)', textTransform: 'uppercase', margin: '0 0 10px', textAlign: 'center' }}>
              Live preview · {f.dims}
            </p>
            <div style={{ width: PREVIEW_W, borderRadius: 14, overflow: 'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.2)', border: '1px solid #ece3d4', margin: '0 auto', position: 'relative' }}>
              <svg
                ref={svgRef}
                viewBox={`0 0 ${CARD_W} ${f.h}`}
                width={PREVIEW_W}
                height={PREVIEW_H}
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'block', background: 'var(--brand-paper)' }}
              >
                <rect width={CARD_W} height={f.h} fill="#faf8f5" />
                {cardNodes}
              </svg>
              {(!imagesReady && neededUrls.length > 0) || !extraLoaded ? (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(250,249,246,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontWeight: 600 }}>Loading...</span>
                </div>
              ) : null}
            </div>
            <button
              onClick={handleDownload}
              disabled={generating || included.length === 0 || !imagesReady || !extraLoaded}
              style={{ width: PREVIEW_W, display: 'block', margin: '16px auto 0', padding: 'var(--space-14)', background: generating || included.length === 0 || !imagesReady || !extraLoaded ? 'var(--neutral-300)' : 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 14, fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: generating || included.length === 0 || !imagesReady || !extraLoaded ? 'default' : 'pointer', boxShadow: '3px 3px 0 rgba(184,92,56,0.2)' }}>
              {done ? '✓ Card saved!' : generating ? 'Generating...' : `Download ${f.label} card`}
            </button>
            <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', textAlign: 'center', margin: '8px 0 0' }}>Arranging this card won't change your actual profile.</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main ShareButton
// ─────────────────────────────────────────────────────────────────────────────
export default function ShareButton({ profile, blocks = [] }) {
  const isMobile = useIsMobile();
  const [open,         setOpen]         = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const dropdownRef = useRef(null);

  const username   = profile?.username || 'reader';
  const profileUrl = `${window.location.origin}/u/${username}`;

  useEffect(() => {
    if (!open || isMobile) return;
    const h = e => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, isMobile]);

  async function handleCopy() {
    try { await navigator.clipboard.writeText(profileUrl); }
    catch { const el = Object.assign(document.createElement('textarea'), { value: profileUrl }); document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
    setCopied(true); setOpen(false);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleShareLink() {
    if (navigator.share) { try { await navigator.share({ title: `@${username} on Reanlo`, url: profileUrl }); } catch (e) { if (e.name !== 'AbortError') handleCopy(); } }
    else handleCopy();
    setOpen(false);
  }

  const OPTIONS = [
    { ic: '🔗', label: copied ? 'Copied!' : 'Copy profile link', sub: profileUrl, act: isMobile && !!navigator.share ? handleShareLink : handleCopy },
    { ic: '🎴', label: 'Build a share card', sub: 'pick size & blocks', hot: true, act: () => { setOpen(false); setShowComposer(true); } },
  ];

  const rows = OPTIONS.map((o, i) => (
    <button key={i} onClick={o.act} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', width: '100%', padding: '12px 14px', background: o.hot ? '#FBF7F0' : 'var(--brand-white)', border: 'none', borderBottom: i < OPTIONS.length - 1 ? '1px solid var(--brand-cream)' : 'none', cursor: 'pointer', textAlign: 'left' }}
      onMouseEnter={e => e.currentTarget.style.background = o.hot ? '#f5ede0' : '#fef9ec'}
      onMouseLeave={e => e.currentTarget.style.background = o.hot ? '#FBF7F0' : '#fff'}>
      <span style={{ fontSize: 'var(--font-size-lg)' }}>{o.ic}</span>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{o.label}{o.hot && <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--brand-rust)', marginLeft: 'var(--space-6)', fontWeight: 700 }}>★</span>}</p>
        {o.sub && <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>{o.sub}</p>}
      </div>
    </button>
  ));

  return (
    <>
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button onClick={() => setOpen(p => !p)}
          style={isMobile
            ? { width: 32, height: 32, borderRadius: 'var(--radius-circle)', background: 'var(--brand-white)', border: '1px solid #f0e0d6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }
            : { display: 'flex', alignItems: 'center', gap: 'var(--space-6)', padding: '8px 16px', background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
          {isMobile
            ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#B85C38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3L12 14"/><path d="M8 7L12 3L16 7"/><path d="M5 11L5 19a1.6 1.6 0 001.6 1.6L17.4 20.6A1.6 1.6 0 0020 19L20 11"/></svg>
            : '↗ Share'}
        </button>

        {!isMobile && open && (
          <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 230, background: 'var(--brand-white)', borderRadius: 14, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', overflow: 'hidden', zIndex: 300, animation: 'shareDropIn 0.18s cubic-bezier(0.16,1,0.3,1)' }}>
            {rows}
          </div>
        )}
        {isMobile && open && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'flex-end' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setOpen(false)} />
            <div style={{ position: 'relative', width: '100%' }}>
              <div style={{ padding: '12px 16px 4px', background: 'var(--brand-white)', borderRadius: '20px 20px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--neutral-100)' }}>
                <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Share Profile</p>
                <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ background: 'var(--brand-white)' }}>{rows}</div>
            </div>
          </div>
        )}
      </div>

      {showComposer && <ShareComposer profile={profile} blocks={blocks} isMobile={isMobile} username={username} profileUrl={profileUrl} onClose={() => setShowComposer(false)} />}

      {copied && (
        <div style={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: 'var(--brand-forest)', color: 'var(--brand-white)', padding: '12px 22px', borderRadius: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
          🔗 Profile link copied
        </div>
      )}
      <style>{`@keyframes shareDropIn{from{opacity:0;transform:translateY(-6px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </>
  );
}